import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  purchasesApi,
  Purchase,
  CreatePurchaseDto,
  UpdatePurchaseDto,
  GetPurchasesDto,
  PurchasesAnalytics,
  PurchasesByStatusAnalytics,
} from '../api/purchases';
import { safeNumber } from '../utils/formatters';

interface UsePurchasesReturn {
  // Data
  purchases: Purchase[];
  analytics: PurchasesAnalytics | null;
  statusAnalytics: PurchasesByStatusAnalytics | null;
  loading: boolean;
  error: string | null;
  
  // Operations
  createPurchase: (data: CreatePurchaseDto) => Promise<Purchase | null>;
  updatePurchase: (id: string, data: UpdatePurchaseDto) => Promise<Purchase | null>;
  deletePurchase: (id: string) => Promise<boolean>;
  refreshData: () => Promise<void>;
  
  // Utility functions
  getPurchaseById: (id: string) => Purchase | undefined;
  getTotalSpent: () => number;
  getPurchasesByStatus: (status: string) => Purchase[];
  getRecentPurchases: (days?: number) => Purchase[];
}

export const usePurchases = (initialFilters: GetPurchasesDto = {}): UsePurchasesReturn => {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [analytics, setAnalytics] = useState<PurchasesAnalytics | null>(null);
  const [statusAnalytics, setStatusAnalytics] = useState<PurchasesByStatusAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load purchases data
  const loadPurchases = useCallback(async (filters: GetPurchasesDto = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const purchasesData = await purchasesApi.getPurchases({ ...initialFilters, ...filters });
      setPurchases(purchasesData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load purchases';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [initialFilters]);

  // Load analytics data
  const loadAnalytics = useCallback(async () => {
    try {
      const [analyticsData, statusData] = await Promise.all([
        purchasesApi.getPurchasesAnalytics(),
        purchasesApi.getPurchasesByStatus(),
      ]);
      
      setAnalytics(analyticsData);
      setStatusAnalytics(statusData);
    } catch (err) {
      console.error('Failed to load purchases analytics:', err);
    }
  }, []);

  // Create purchase
  const createPurchase = useCallback(async (data: CreatePurchaseDto): Promise<Purchase | null> => {
    try {
      setLoading(true);
      const newPurchase = await purchasesApi.createPurchase(data);
      
      setPurchases(prev => [newPurchase, ...prev]);
      toast.success('Purchase created successfully');
      
      // Refresh analytics
      loadAnalytics();
      
      return newPurchase;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create purchase';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadAnalytics]);

  // Update purchase
  const updatePurchase = useCallback(async (id: string, data: UpdatePurchaseDto): Promise<Purchase | null> => {
    try {
      setLoading(true);
      const updatedPurchase = await purchasesApi.updatePurchase(id, data);
      
      setPurchases(prev => 
        prev.map(purchase => 
          purchase.id === id ? updatedPurchase : purchase
        )
      );
      
      toast.success('Purchase updated successfully');
      
      // Refresh analytics
      loadAnalytics();
      
      return updatedPurchase;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update purchase';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadAnalytics]);

  // Delete purchase
  const deletePurchase = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      await purchasesApi.deletePurchase(id);
      
      setPurchases(prev => prev.filter(purchase => purchase.id !== id));
      toast.success('Purchase deleted successfully');
      
      // Refresh analytics
      loadAnalytics();
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete purchase';
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadAnalytics]);

  // Refresh data
  const refreshData = useCallback(async () => {
    await Promise.all([
      loadPurchases(),
      loadAnalytics(),
    ]);
  }, [loadPurchases, loadAnalytics]);

  // Utility functions
  const getPurchaseById = useCallback((id: string): Purchase | undefined => {
    return purchases.find(purchase => purchase.id === id);
  }, [purchases]);

  const getTotalSpent = useCallback((): number => {
    return purchases.reduce((total, purchase) => total + safeNumber(purchase.total), 0);
  }, [purchases]);

  const getPurchasesByStatus = useCallback((status: string): Purchase[] => {
    return purchases.filter(purchase => purchase.status === status);
  }, [purchases]);

  const getRecentPurchases = useCallback((days = 30): Purchase[] => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    return purchases.filter(purchase => new Date(purchase.purchaseDate) >= cutoffDate);
  }, [purchases]);

  // Initialize data loading
  useEffect(() => {
    refreshData();
  }, []); // Empty dependency array means this runs once on mount

  return {
    // Data
    purchases,
    analytics,
    statusAnalytics,
    loading,
    error,
    
    // Operations
    createPurchase,
    updatePurchase,
    deletePurchase,
    refreshData,
    
    // Utility functions
    getPurchaseById,
    getTotalSpent,
    getPurchasesByStatus,
    getRecentPurchases,
  };
}; 